import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Gem } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

// Hero slides cache key — caches the curated slide list across visits so a
// returning customer instantly sees the same first slide they last saw,
// with NO flash of a different image before the API resolves. First-time
// visitors see the clean navy backdrop + brand headline only.
const HERO_CACHE_KEY = "bhola_hero_slides_v1";
const HERO_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24; // 24 h

const readHeroCache = () => {
  try {
    const raw = typeof window !== "undefined" && localStorage.getItem(HERO_CACHE_KEY);
    if (!raw) return [];
    const obj = JSON.parse(raw);
    if (!obj || !Array.isArray(obj.slides) || !obj.ts) return [];
    if (Date.now() - obj.ts > HERO_CACHE_MAX_AGE_MS) return [];
    return obj.slides;
  } catch (err) { console.warn("[HeroCarousel] cache read failed:", err); return []; }
};

const writeHeroCache = (slides) => {
  try {
    if (typeof window !== "undefined") {
      localStorage.setItem(HERO_CACHE_KEY, JSON.stringify({ ts: Date.now(), slides }));
    }
  } catch (err) { console.warn("[HeroCarousel] cache write failed:", err); /* quota or disabled storage */ }
};

const AUTOPLAY_MS = 6000;

/**
 * Editorial hero — image + restrained text overlay.
 * - Headline & CTAs anchored to the left half over a dark gradient,
 *   guaranteeing legibility regardless of the underlying image.
 * - Right half deliberately clear so the jewellery reads at full quality.
 * - Subtle product credit at bottom-right (auto-updates with active slide).
 * - object-fit:contain on a navy backdrop — no awkward cropping for
 *   portrait product photos.
 */
export default function HeroCarousel() {
  // Synchronous read of localStorage during initial render keeps a
  // returning customer's last-seen first slide visible on the very first
  // paint — no flash, no blank state.
  const [slides, setSlides] = useState(() => readHeroCache());
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Pull a Ringstudio piece AND catalogue items in parallel, then
        // splice 1 Ringstudio image to the front + pick 5 diverse catalogue
        // pieces after it (6 slides total).
        const [ringRes, catRes] = await Promise.all([
          api.get("/items", { params: { limit: 60, sort: "newest", collection: "ringstudio" } }).catch(() => ({ data: { items: [] } })),
          api.get("/items", { params: { limit: 250, sort: "newest" } }).catch(() => ({ data: { items: [] } })),
        ]);
        const KNOWN_PLACEHOLDERS = new Set(["A003"]);

        // Pick the best Ringstudio piece (most diamond carat) with an image.
        const ringPool = (ringRes.data.items || []).filter((it) => it.image_path && !KNOWN_PLACEHOLDERS.has(it.tag_no));
        const ringBest = ringPool.sort(
          (a, b) => (b.diamond_weight || 0) * 100 + (b.gold_weight || 0) * 2 - ((a.diamond_weight || 0) * 100 + (a.gold_weight || 0) * 2)
        )[0];

        // Catalogue pool — exclude Ringstudio so we don't repeat the same piece.
        const catPool = (catRes.data.items || []).filter(
          (it) => it.image_path && !KNOWN_PLACEHOLDERS.has(it.tag_no) && it.collection !== "ringstudio"
        );
        const scored = catPool
          .map((it) => ({
            it,
            score:
              (it.diamond_weight || 0) * 100 +
              (it.gold_weight || 0) * 2 +
              (it.no_of_diamonds || 0) * 0.5,
          }))
          .sort((a, b) => b.score - a.score);

        const TARGET = 5;
        const seen = new Map();
        const ranked = [];
        for (const { it } of scored) {
          const cat = (it.category || "uncategorised").toLowerCase();
          const count = seen.get(cat) || 0;
          if (count >= 2) continue; // max 2 per category for variety
          seen.set(cat, count + 1);
          ranked.push(it);
          if (ranked.length >= TARGET) break;
        }

        const combined = [];
        if (ringBest) combined.push({ ...ringBest, _ringstudio: true });
        combined.push(...ranked);
        if (cancelled || combined.length === 0) return;

        const nextSlides = combined.map((it) => ({
          src: fileUrl(it.image_path),
          name: it.item_name,
          tag: it.tag_no,
          karat: it.karat,
          ringstudio: !!it._ringstudio,
        }));
        setSlides(nextSlides);
        writeHeroCache(nextSlides);
      } catch (err) { console.error("[HeroCarousel] slide fetch failed:", err); /* keep cached / empty state — no rogue image */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (paused || slides.length < 2) return;
    timerRef.current = setTimeout(() => setIdx((i) => (i + 1) % slides.length), AUTOPLAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [idx, paused, slides.length]);

  const goPrev = () => setIdx((i) => (i - 1 + slides.length) % slides.length);
  const goNext = () => setIdx((i) => (i + 1) % slides.length);

  // Touch swipe for mobile — finger drag changes slides.
  const touchRef = useRef({ x: 0, y: 0, t: 0 });
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    setPaused(true);
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;
    if (dt < 600 && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) {
      if (dx < 0) goNext(); else goPrev();
    }
    setTimeout(() => setPaused(false), 800);
  };

  const current = slides[idx] || {};

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ background: "#050B1F" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-carousel"
    >
      {/*
        Layout strategy:
        - MOBILE (<md): the image owns the top half (h ~ 56vh, capped at 480px),
          and the headline + CTAs stack BELOW in a clean white block.
          No tricky overlays on a 375 px screen.
        - DESKTOP (>=md): image fills an 82vh frame, the headline + CTAs
          overlay the LEFT half over a dark gradient.
      */}

      {/* IMAGE LAYER */}
      <div
        className="relative w-full"
        style={{
          // Heights — mobile small, desktop tall
          // Using clamp keeps it sane across the spectrum.
          height: "clamp(320px, 56vh, 460px)",
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {slides.map((s, i) => (
          <div
            key={`m-${s.tag || "fallback"}-${i}`}
            className="absolute inset-0 transition-opacity duration-[1400ms] ease-in-out md:hidden"
            style={{ opacity: i === idx ? 1 : 0, zIndex: i === idx ? 1 : 0 }}
            aria-hidden={i !== idx}
          >
            {s.src ? (
              <img
                src={s.src}
                alt=""
                className="w-full h-full"
                style={{ objectFit: "contain", objectPosition: "center" }}
                loading={i === 0 ? "eager" : "lazy"}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Gem className="w-20 h-20 text-gold/30" /></div>
            )}
          </div>
        ))}

        {/* Mobile dots — anchored to the image, not the page */}
        {slides.length > 1 && (
          <div className="md:hidden absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-testid="hero-dots-mobile">
            {slides.map((s, i) => (
              <button
                key={`m-dot-${s.tag || i}`}
                onClick={() => setIdx(i)}
                aria-label={`Go to slide ${i + 1}`}
                className="h-1 transition-all duration-300"
                style={{
                  width: i === idx ? 24 : 10,
                  background: i === idx ? "#D9BE6E" : "rgba(250,246,238,0.45)",
                }}
              />
            ))}
          </div>
        )}

        {/* Mobile prev/next — small, semi-transparent, edges of the image */}
        {slides.length > 1 && (
          <>
            <button
              onClick={goPrev}
              aria-label="Previous slide"
              className="md:hidden absolute left-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 flex items-center justify-center"
              style={{ background: "rgba(5,11,31,0.5)", border: "1px solid rgba(217,190,110,0.4)" }}
            >
              <ChevronLeft className="w-4 h-4" style={{ color: "#FAF6EE" }} />
            </button>
            <button
              onClick={goNext}
              aria-label="Next slide"
              className="md:hidden absolute right-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 flex items-center justify-center"
              style={{ background: "rgba(5,11,31,0.5)", border: "1px solid rgba(217,190,110,0.4)" }}
            >
              <ChevronRight className="w-4 h-4" style={{ color: "#FAF6EE" }} />
            </button>
          </>
        )}
      </div>

      {/* MOBILE TEXT BLOCK — stacked below the image */}
      <div className="md:hidden" style={{ background: "#050B1F" }}>
        {/* Product info immediately under the image (this is the active slide's piece) */}
        {current.name && (
          <div
            className="px-6 pt-5 pb-4 border-b text-center"
            style={{ borderColor: "rgba(217,190,110,0.18)" }}
            data-testid="hero-product-credit-mobile"
            aria-live="polite"
          >
            <div className="text-[9px] tracking-[0.35em] uppercase" style={{ color: "rgba(217,190,110,0.75)" }}>Featured Piece</div>
            <div className="font-display text-base mt-1.5 leading-tight" style={{ color: "#FAF6EE", fontWeight: 500 }}>
              {current.name}
            </div>
            <div className="text-[10px] tracking-[0.28em] uppercase mt-1.5" style={{ color: "rgba(250,246,238,0.55)" }}>
              {current.tag}{current.karat ? ` · ${current.karat}` : ""}
            </div>
          </div>
        )}
        <div className="px-6 pt-9 pb-10">
          <div className="text-[10px] tracking-[0.4em] uppercase mb-4" style={{ color: "#D9BE6E" }}>
            <span className="inline-block w-6 h-px align-middle mr-2" style={{ background: "#D9BE6E" }} />
            Chandigarh · Since 1966
          </div>
          <h1 className="font-display leading-[1.1]" style={{ fontWeight: 400, fontSize: "clamp(2rem, 8vw, 2.75rem)", color: "#FAF6EE" }}>
            A quieter kind of luxury,<br />
            <em className="font-light" style={{ fontStyle: "italic", color: "#D9BE6E" }}>in gold and light.</em>
          </h1>
          <p className="mt-5 text-sm leading-relaxed" style={{ color: "rgba(250,246,238,0.78)" }}>
            Four generations of goldsmiths in the heart of Chandigarh — hallmarked gold and certified diamond
            jewellery, priced honestly against today&apos;s wholesale market rate.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <Link
              to="/catalogue"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 tracking-[0.2em] text-xs uppercase font-medium"
              style={{ background: "#C9A84C", color: "#0B1A33" }}
              data-testid="hero-shop-cta-mobile"
            >
              Browse the Collection <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/solitaires"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 tracking-[0.2em] text-xs uppercase border"
              style={{ borderColor: "rgba(217,190,110,0.55)", color: "#FAF6EE" }}
              data-testid="hero-diamonds-cta-mobile"
            >
              <Gem className="w-4 h-4" style={{ color: "#D9BE6E" }} /> Signature Solitaires
            </Link>
          </div>
        </div>
      </div>

      {/* DESKTOP OVERLAY — only renders on md+ */}
      <div
        className="hidden md:block absolute inset-0"
        style={{ height: "82vh", maxHeight: "780px" }}
      >
        {slides.map((s, i) => (
          <div
            key={`d-${s.tag || "fallback"}-${i}`}
            className="absolute inset-0 transition-opacity duration-[1400ms] ease-in-out"
            style={{ opacity: i === idx ? 1 : 0, zIndex: i === idx ? 1 : 0 }}
            aria-hidden={i !== idx}
          >
            {s.src ? (
              <img
                src={s.src}
                alt=""
                className="w-full h-full"
                style={{ objectFit: "contain", objectPosition: "70% center" }}
                loading={i === 0 ? "eager" : "lazy"}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Gem className="w-24 h-24 text-gold/30" /></div>
            )}
          </div>
        ))}

        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(90deg, rgba(5,11,31,0.95) 0%, rgba(5,11,31,0.78) 30%, rgba(5,11,31,0.25) 60%, rgba(5,11,31,0.0) 80%)", zIndex: 5 }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, transparent 18%, transparent 78%, rgba(0,0,0,0.28) 100%)", zIndex: 5 }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-12 h-full flex items-center">
          <div className="max-w-xl fade-up">
            <div className="text-[11px] tracking-[0.4em] uppercase mb-6" style={{ color: "#D9BE6E" }}>
              <span className="inline-block w-8 h-px align-middle mr-3" style={{ background: "#D9BE6E" }} />
              Chandigarh · Since 1966
            </div>
            <h1 className="font-display leading-[1.06]" style={{ fontWeight: 400, fontSize: "clamp(2.25rem, 4.8vw, 4.25rem)", color: "#FAF6EE" }}>
              A quieter kind of luxury,<br />
              <em className="font-light" style={{ fontStyle: "italic", color: "#D9BE6E" }}>in gold and light.</em>
            </h1>
            <p className="mt-6 max-w-md text-[15px] md:text-base leading-relaxed" style={{ color: "rgba(250,246,238,0.78)" }}>
              Four generations of goldsmiths in the heart of Chandigarh — hallmarked gold and certified
              diamond jewellery, priced honestly against today&apos;s wholesale market rate.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/catalogue"
                className="inline-flex items-center gap-3 px-6 py-3.5 tracking-[0.2em] text-xs uppercase font-medium transition-colors"
                style={{ background: "#C9A84C", color: "#0B1A33" }}
                data-testid="hero-shop-cta"
              >
                Browse the Collection <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/solitaires"
                className="inline-flex items-center gap-3 px-6 py-3.5 tracking-[0.2em] text-xs uppercase border transition-colors hover:bg-gold/10"
                style={{ borderColor: "rgba(217,190,110,0.55)", color: "#FAF6EE" }}
                data-testid="hero-diamonds-cta"
              >
                <Gem className="w-4 h-4" style={{ color: "#D9BE6E" }} /> Signature Solitaires
              </Link>
            </div>
          </div>
        </div>

        {current.name && (
          <div className="absolute bottom-7 right-12 z-10 text-right pointer-events-none" data-testid="hero-product-credit">
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: "rgba(217,190,110,0.7)" }}>Featured</div>
            <div className="font-display text-base mt-1" style={{ color: "rgba(250,246,238,0.9)", fontWeight: 500 }}>{current.name}</div>
            <div className="text-[10px] tracking-[0.32em] uppercase mt-1" style={{ color: "rgba(250,246,238,0.55)" }}>{current.tag} · {current.karat}</div>
          </div>
        )}

        {slides.length > 1 && (
          <>
            <button
              onClick={goPrev}
              aria-label="Previous slide"
              className="absolute left-5 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center transition-all duration-300 hover:scale-110"
              style={{ background: "rgba(5,11,31,0.5)", border: "1px solid rgba(217,190,110,0.4)", backdropFilter: "blur(6px)" }}
              data-testid="hero-prev"
            >
              <ChevronLeft className="w-5 h-5" style={{ color: "#FAF6EE" }} />
            </button>
            <button
              onClick={goNext}
              aria-label="Next slide"
              className="absolute right-5 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center transition-all duration-300 hover:scale-110"
              style={{ background: "rgba(5,11,31,0.5)", border: "1px solid rgba(217,190,110,0.4)", backdropFilter: "blur(6px)" }}
              data-testid="hero-next"
            >
              <ChevronRight className="w-5 h-5" style={{ color: "#FAF6EE" }} />
            </button>
            <div className="absolute bottom-5 left-12 z-20 flex gap-3" data-testid="hero-dots">
              {slides.map((s, i) => (
                <button
                  key={`d-dot-${s.tag || i}`}
                  onClick={() => setIdx(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className="h-1 transition-all duration-300"
                  style={{
                    width: i === idx ? 32 : 12,
                    background: i === idx ? "#D9BE6E" : "rgba(250,246,238,0.45)",
                  }}
                  data-testid={`hero-dot-${i}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Desktop frame spacer — reserves the 82vh height the absolute overlay needs */}
      <div className="hidden md:block" style={{ height: "82vh", maxHeight: "780px" }} aria-hidden />
    </section>
  );
}
